import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { EXPLORA_DESTINATIONS } from "@/lib/explora-destinations";

export const HERO_DESTINATION_STORAGE_KEY = "mm:hero:destination";
export type HeroDestinationSlug = "all" | string;

export interface HeroDestinationSelectMeta {
  tourCount?: number;
  source?: "map" | "chips" | "spotlight" | "external" | "init";
}

interface HeroDestinationContextValue {
  selectedDestination: HeroDestinationSlug;
  selectDestination: (
    destination: HeroDestinationSlug,
    meta?: HeroDestinationSelectMeta,
  ) => void;
  isValidDestination: (destination: HeroDestinationSlug) => boolean;
}

const HeroDestinationContext = createContext<HeroDestinationContextValue | null>(
  null,
);

function normalizeDestination(destination?: string | null): HeroDestinationSlug | null {
  if (!destination || destination === "all") return "all";
  const match = EXPLORA_DESTINATIONS.find(
    (d) => d.slug === destination || d.citySlug === destination,
  );
  return match?.slug ?? null;
}

function readInitialDestination(): HeroDestinationSlug {
  if (typeof window === "undefined") return "all";

  // Only honor an explicit URL override on fresh page load. We intentionally
  // do NOT read from localStorage here so that returning to the Home always
  // shows Maya + her quick-access menu in the initial "all" state.
  const params = new URLSearchParams(window.location.search);
  const fromUrl = normalizeDestination(params.get("destino"));
  if (fromUrl) return fromUrl;

  return "all";
}


export function HeroDestinationProvider({ children }: { children: ReactNode }) {
  const [selectedDestination, setSelectedDestination] =
    useState<HeroDestinationSlug>("all");

  const emitDestination = useCallback(
    (destination: HeroDestinationSlug, meta: HeroDestinationSelectMeta = {}) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("holomap:destinationChanged", {
          detail: {
            destination,
            tourCount: meta.tourCount,
            source: meta.source ?? "external",
          },
        }),
      );
    },
    [],
  );

  const selectDestination = useCallback(
    (destination: HeroDestinationSlug, meta: HeroDestinationSelectMeta = {}) => {
      const normalized = normalizeDestination(destination);
      if (!normalized) return;

      setSelectedDestination(normalized);

      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(HERO_DESTINATION_STORAGE_KEY, normalized);
        } catch {
          /* noop */
        }
      }

      emitDestination(normalized, meta);
    },
    [emitDestination],
  );

  useEffect(() => {
    const initial = readInitialDestination();
    setSelectedDestination(initial);
    emitDestination(initial, { source: "init" });
  }, [emitDestination]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onLegacySet = (e: Event) => {
      const detail = (
        e as CustomEvent<{ destination?: string; tourCount?: number }>
      ).detail;
      if (!detail?.destination) return;
      selectDestination(detail.destination, {
        tourCount: detail.tourCount,
        source: "external",
      });
    };

    window.addEventListener("holomap:setDestination", onLegacySet as EventListener);
    return () =>
      window.removeEventListener(
        "holomap:setDestination",
        onLegacySet as EventListener,
      );
  }, [selectDestination]);

  const isValidDestination = useCallback(
    (destination: HeroDestinationSlug) => normalizeDestination(destination) != null,
    [],
  );

  const value = useMemo(
    () => ({ selectedDestination, selectDestination, isValidDestination }),
    [isValidDestination, selectDestination, selectedDestination],
  );

  return (
    <HeroDestinationContext.Provider value={value}>
      {children}
    </HeroDestinationContext.Provider>
  );
}

export function useHeroDestination() {
  const context = useContext(HeroDestinationContext);
  if (!context) {
    throw new Error("useHeroDestination must be used inside HeroDestinationProvider");
  }
  return context;
}